import * as _ from 'lodash-es';
import {
  createContext,
  useContext,
  useEffect,
  useLayoutEffect,
  useState,
} from 'react';
import { useEffectEvent } from 'use-effect-event';
import { useSidebarOpenState } from '../app/layout/SidebarOpenStateContext';
import useGetLayoutBaseWidth from '../app/layout/useGetLayoutBaseWidth';
import { SIDEBAR_SHIFT_BREAKPOINT } from '../post/PostLayoutSizes';
import * as styles from './useMainContentWidthSelector.module.scss';

// eslint-disable-next-line import/no-unused-modules -- expose for testing
export const StoryDemoWidthContext = createContext<number | undefined>(
  undefined,
);

const SIDEBAR_WIDTH = parseInt(styles.sidebarWidth, 10);

const getWidthExcludingSidebar = (width: number, isSidebarOpen: boolean) => {
  // If the width is less than the sidebar shift breakpoint, we don't need to
  // consider the sidebar width as it won't take up space in the layout
  if (width < SIDEBAR_SHIFT_BREAKPOINT) return width;
  return width - (isSidebarOpen ? SIDEBAR_WIDTH : 0);
};

/**
 * Returns the window width minus the sidebar width when it's open minus the right panel width
 * when it's available and pinned. Takes a selector function that transforms the calculated width
 * into a value of type T. Using a selector function prevents unnecessary re-renders by only updating
 * when the transformed value changes, not on every resize event.
 *
 * @example
 * // Returns true if width (excluding sidebar and pinned right panel) is less than 768px
 * const isNarrow = useMainContentWidthSelector(width => width < 768);
 */
const useMainContentWidthSelector = <T>(selector: (width: number) => T): T => {
  const storyDemoWidth = useContext(StoryDemoWidthContext);
  const { isSidebarOpen } = useSidebarOpenState();
  const getLayoutBaseWidth = useGetLayoutBaseWidth();
  const [state, setState] = useState(() =>
    selector(getWidthExcludingSidebar(getLayoutBaseWidth(), isSidebarOpen)),
  );

  const handleResize = useEffectEvent(() => {
    const next = selector(
      getWidthExcludingSidebar(getLayoutBaseWidth(), isSidebarOpen),
    );

    setState((current) => (_.isEqual(current, next) ? current : next));
  });

  // Both the layout base width and the sidebar open state can
  // change the width of the main content, so we need to trigger a
  // resize when either changes. Using useLayoutEffect prevents flickering.
  useLayoutEffect(() => {
    handleResize();
  }, [getLayoutBaseWidth, isSidebarOpen]);

  useEffect(() => {
    handleResize();

    window.addEventListener('resize', handleResize, { passive: true });
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  return storyDemoWidth == null ? state : selector(storyDemoWidth);
};

export default useMainContentWidthSelector;
